import { useRef, useState } from 'react';

import { Bot, Download, Pencil, Plus, Power, PowerOff, Trash2, Upload } from 'lucide-react';

import { DeleteConfirmationDialog } from '@/components/shared/DeleteConfirmationDialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { useToast } from '@/hooks/use-toast';
import {
  useAgentDefinitions,
  useCreateAgentDefinition,
  useDeleteAgentDefinition,
  useUpdateAgentDefinition,
} from '@/hooks/useAgentDefinitions';
import { usePageTitle } from '@/hooks/usePageTitle';
import { useCustomTools } from '@/hooks/useTools';
import { extractErrorMessage, extractValidationErrors } from '@/lib/utils';

import { AgentDefinitionModal } from './AgentDefinitionModal';
import { buildBundle, downloadJson, parseBundle, readFileAsText } from './io';

import type { ValidationError } from '@/lib/utils';
import type { AgentDefinition, CreateAgentDefinitionDto } from '@repo/shared-types';

export const AgentDefinitionsPage = () => {
  usePageTitle('Agent Definitions');
  const { data: agents = [], isLoading, error } = useAgentDefinitions();
  const { data: customToolsData } = useCustomTools({
    page: 1,
    limit: 100,
    sortBy: 'name',
    sortOrder: 'asc',
  });
  const customToolById = new Map((customToolsData?.data ?? []).map((t) => [t.id, t] as const));
  const createMutation = useCreateAgentDefinition();
  const updateMutation = useUpdateAgentDefinition();
  const deleteMutation = useDeleteAgentDefinition();

  const [editingAgent, setEditingAgent] = useState<AgentDefinition | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<AgentDefinition | null>(null);
  const [modalErrors, setModalErrors] = useState<ValidationError[]>([]);
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { toast } = useToast();

  const handleNew = () => {
    setEditingAgent(null);
    setModalErrors([]);
    setIsModalOpen(true);
  };

  const handleEdit = (agent: AgentDefinition) => {
    setEditingAgent(agent);
    setModalErrors([]);
    setIsModalOpen(true);
  };

  const handleModalOpenChange = (open: boolean) => {
    setIsModalOpen(open);
    if (!open) {
      setModalErrors([]);
      setEditingAgent(null);
    }
  };

  const handleSubmit = async (values: CreateAgentDefinitionDto) => {
    try {
      setModalErrors([]);
      setIsSaving(true);

      if (editingAgent) {
        await updateMutation.mutateAsync({ id: editingAgent.id, data: values });
        toast({
          variant: 'success',
          title: 'Agent updated',
          description: `"${values.name}" has been updated successfully.`,
        });
      } else {
        await createMutation.mutateAsync(values);
        toast({
          variant: 'success',
          title: 'Agent created',
          description: `"${values.name}" has been created successfully.`,
        });
      }

      setEditingAgent(null);
      setIsModalOpen(false);
    } catch (err) {
      const validationErrors = extractValidationErrors(err);
      if (validationErrors.length > 0) {
        setModalErrors(validationErrors);
      } else {
        toast({
          variant: 'destructive',
          title: editingAgent ? 'Failed to update agent' : 'Failed to create agent',
          description: extractErrorMessage(err),
        });
      }
    } finally {
      setIsSaving(false);
    }
  };

  const slugify = (input: string) =>
    input
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '')
      .slice(0, 40) || 'agents';

  const handleExportSingle = (agent: AgentDefinition) => {
    const bundle = buildBundle([agent]);
    downloadJson(`agent-${slugify(agent.name)}.json`, bundle);
  };

  const handleExportAll = () => {
    if (agents.length === 0) {
      toast({
        variant: 'destructive',
        title: 'Nothing to export',
        description: 'Create at least one agent first.',
      });
      return;
    }
    const bundle = buildBundle(agents);
    downloadJson(`agent-definitions-${new Date().toISOString().slice(0, 10)}.json`, bundle);
    toast({
      variant: 'success',
      title: 'Exported',
      description: `Downloaded ${agents.length} agent${agents.length === 1 ? '' : 's'}.`,
    });
  };

  const handleImportClick = () => {
    fileInputRef.current?.click();
  };

  const handleImportFile = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;

    try {
      setIsImporting(true);
      const text = await readFileAsText(file);
      const bundle = parseBundle(text);

      const existingNames = new Set(agents.map((a) => a.name));
      const toCreate: CreateAgentDefinitionDto[] = [];
      const skipped: string[] = [];

      for (const candidate of bundle.agents) {
        if (existingNames.has(candidate.name)) {
          skipped.push(candidate.name);
        } else {
          toCreate.push(candidate);
        }
      }

      let createdCount = 0;
      const failed: string[] = [];
      for (const dto of toCreate) {
        try {
          await createMutation.mutateAsync(dto);
          createdCount++;
        } catch (err) {
          failed.push(`${dto.name} (${extractErrorMessage(err)})`);
        }
      }

      const parts: string[] = [];
      if (createdCount > 0) parts.push(`Imported ${createdCount}`);
      if (skipped.length > 0) parts.push(`skipped ${skipped.length} (name already exists)`);
      if (failed.length > 0) parts.push(`${failed.length} failed`);

      toast({
        variant: failed.length > 0 ? 'destructive' : 'success',
        title: failed.length > 0 ? 'Import completed with errors' : 'Import complete',
        description:
          (parts.join(' · ') || 'No agents to import') +
          (failed.length > 0 ? `\nFailures: ${failed.join('; ')}` : ''),
      });
    } catch (err) {
      toast({
        variant: 'destructive',
        title: 'Import failed',
        description: extractErrorMessage(err),
      });
    } finally {
      setIsImporting(false);
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      setIsDeleting(true);
      await deleteMutation.mutateAsync(deleteTarget.id);
      setDeleteTarget(null);
      toast({
        variant: 'success',
        title: 'Agent deleted',
        description: `"${deleteTarget.name}" has been deleted.`,
      });
    } catch (err) {
      toast({
        variant: 'destructive',
        title: 'Failed to delete agent',
        description: extractErrorMessage(err),
      });
    } finally {
      setIsDeleting(false);
    }
  };

  if (error) {
    return (
      <div className="flex min-h-[400px] items-center justify-center">
        <div className="text-center">
          <Bot className="text-destructive mx-auto h-12 w-12 opacity-50" />
          <p className="text-destructive mt-4 font-medium">Failed to load agent definitions</p>
          <p className="text-muted-foreground mt-2 text-sm">{extractErrorMessage(error)}</p>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex min-h-[400px] items-center justify-center">
        <div className="text-center">
          <div className="border-primary mx-auto h-12 w-12 animate-spin rounded-full border-b-2" />
          <p className="text-muted-foreground mt-4">Loading agent definitions...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <header className="flex items-start justify-between">
        <div className="space-y-2">
          <p className="text-muted-foreground text-sm tracking-[0.3em] uppercase">
            Multi-Agent System
          </p>
          <h1 className="text-3xl font-bold tracking-tight">Agent Definitions</h1>
          <p className="text-muted-foreground max-w-3xl">
            Define specialised AI agents that can hand off live calls to each other. Each agent has
            its own prompt, tools, and (optional) voice. Assign agents to a configuration on the
            Configurations page to enable multi-agent calls.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <input
            ref={fileInputRef}
            type="file"
            accept="application/json,.json"
            onChange={handleImportFile}
            className="hidden"
          />
          <Button variant="outline" onClick={handleImportClick} disabled={isImporting}>
            <Upload className="h-4 w-4" />
            {isImporting ? 'Importing...' : 'Import JSON'}
          </Button>
          <Button variant="outline" onClick={handleExportAll} disabled={agents.length === 0}>
            <Download className="h-4 w-4" />
            Export All
          </Button>
          <Button onClick={handleNew}>
            <Plus className="h-4 w-4" />
            New Agent
          </Button>
        </div>
      </header>

      <Card>
        <CardHeader>
          <CardTitle>Agents</CardTitle>
          <CardDescription>
            Manage agent definitions. Assign agents to configurations via the Configuration
            settings.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {agents.length === 0 ? (
            <div className="text-muted-foreground flex min-h-[200px] flex-col items-center justify-center space-y-4 py-8 text-center">
              <Bot className="h-12 w-12 opacity-50" />
              <div>
                <p className="font-medium">No agent definitions yet</p>
                <p className="text-sm">Create your first agent to get started.</p>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              {agents.map((agent) => (
                <div key={agent.id} className="rounded-lg border p-4">
                  <div className="flex flex-col gap-2 md:flex-row md:items-start md:justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <p className="font-mono text-lg font-semibold">{agent.name}</p>
                        {agent.is_active ? (
                          <Badge variant="default" className="gap-1">
                            <Power className="h-3 w-3" />
                            Active
                          </Badge>
                        ) : (
                          <Badge variant="secondary" className="gap-1">
                            <PowerOff className="h-3 w-3" />
                            Inactive
                          </Badge>
                        )}
                      </div>
                      <p className="text-muted-foreground mt-1 text-sm">{agent.role}</p>
                      <div className="mt-2 flex flex-wrap gap-1">
                        {(agent.tools as string[]).map((tool) => (
                          <Badge key={tool} variant="outline" className="text-xs">
                            {tool}
                          </Badge>
                        ))}
                        {(agent.custom_tool_ids ?? []).map((id) => {
                          const tool = customToolById.get(id);
                          const label = tool?.name ?? `(missing tool ${id.slice(0, 8)})`;
                          return (
                            <Badge
                              key={id}
                              variant="secondary"
                              className="font-mono text-xs"
                              title={
                                tool
                                  ? (tool.description ?? 'Custom tool')
                                  : 'Custom tool not found in this environment'
                              }
                            >
                              {label}
                            </Badge>
                          );
                        })}
                        {agent.voice_name && (
                          <Badge variant="outline" className="text-xs">
                            Voice: {agent.voice_name}
                          </Badge>
                        )}
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleExportSingle(agent)}
                        title="Export this agent as JSON"
                      >
                        <Download className="h-4 w-4" />
                        Export
                      </Button>
                      <Button variant="outline" size="sm" onClick={() => handleEdit(agent)}>
                        <Pencil className="h-4 w-4" />
                        Edit
                      </Button>
                      <Button
                        variant="destructive"
                        size="sm"
                        onClick={() => setDeleteTarget(agent)}
                      >
                        <Trash2 className="h-4 w-4" />
                        Delete
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <AgentDefinitionModal
        open={isModalOpen}
        onOpenChange={handleModalOpenChange}
        agent={editingAgent}
        onSubmit={handleSubmit}
        errors={modalErrors}
        isLoading={isSaving}
      />

      <DeleteConfirmationDialog
        open={Boolean(deleteTarget)}
        resourceName="agent definition"
        resourceIdentifier={deleteTarget?.name}
        onCancel={() => setDeleteTarget(null)}
        onConfirm={confirmDelete}
        isLoading={isDeleting}
      />
    </div>
  );
};
